import { useEffect, useRef } from 'react';
import type { Dispatch } from '@reduxjs/toolkit';

import {
  getAudioOverview,
  toAudioOverviewError,
} from '@/services/audioOverviews';
import {
  articleAudioOverviewDropped,
  audioOverviewRemoved,
  audioOverviewsUpserted,
  nextAudioOverviewStamp,
} from '@/slices/audioOverviews/audioOverviewsSlice';
import type { AudioOverviewSummary } from '@/types/AudioOverview';
import { loadAudioOverviewConfig } from '../hooks/useAudioOverviewConfig';

export const AUDIO_OVERVIEW_POLL_INTERVAL_MS = 5_000;
export const AUDIO_OVERVIEW_POLL_MAX_BACKOFF_MS = 60_000;

/** 5 s, doubling after each failed round, capped at 60 s. */
export const audioOverviewPollDelay = (failures: number): number =>
  Math.min(
    AUDIO_OVERVIEW_POLL_INTERVAL_MS * 2 ** failures,
    AUDIO_OVERVIEW_POLL_MAX_BACKOFF_MS
  );

const isDocumentHidden = () =>
  typeof document !== 'undefined' && document.visibilityState === 'hidden';

interface UseAudioOverviewPollingOptions {
  /** Sorted, comma-joined ids of the overviews still generating. */
  pendingKey: string;
  enabled: boolean;
  dispatch: Dispatch;
}

/**
 * Polls `GET /:id` for every overview still generating (ORA-735):
 *
 * - one round at a time (a slow round is never overlapped by the next);
 * - backs off exponentially while rounds fail, up to 60 s;
 * - pauses while the tab is hidden and polls at once when it is visible again;
 * - stops when nothing is generating, the feature is off, or on unmount
 *   (the watcher unmounts on logout);
 * - a 404 drops that overview and re-reads `/config`, so a feature switched
 *   off mid-session hides everywhere instead of failing piecemeal;
 * - a 403 UPGRADE_REQUIRED drops an article episode (ORA-905).
 */
export const useAudioOverviewPolling = ({
  pendingKey,
  enabled,
  dispatch,
}: UseAudioOverviewPollingOptions): void => {
  // Shared across effect runs: the pending set changes as rounds land, and a
  // round still in flight must not be doubled by the next run's first round.
  const inFlightRef = useRef(false);
  const failuresRef = useRef(0);

  useEffect(() => {
    if (!enabled || !pendingKey) {
      failuresRef.current = 0;
      return;
    }

    const ids = pendingKey.split(',');
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const clearTimer = () => {
      if (timer === null) return;
      clearTimeout(timer);
      timer = null;
    };

    const schedule = () => {
      if (cancelled || timer !== null || isDocumentHidden()) return;

      timer = setTimeout(() => {
        timer = null;
        void poll();
      }, audioOverviewPollDelay(failuresRef.current));
    };

    const poll = async () => {
      if (cancelled) return;
      if (inFlightRef.current) {
        schedule();
        return;
      }

      inFlightRef.current = true;
      const requestedAt = nextAudioOverviewStamp();

      const results = await Promise.allSettled(
        ids.map((id) => getAudioOverview(id))
      );

      inFlightRef.current = false;

      // Landed data is good even if this run was superseded; the stamps keep
      // it from overwriting anything newer.
      const items: AudioOverviewSummary[] = [];
      let failed = false;
      let wasRemoved = false;

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          items.push(result.value);
          return;
        }

        const code = toAudioOverviewError(result.reason).code;
        if (code === 'NOT_FOUND') {
          dispatch(audioOverviewRemoved(ids[index]));
          wasRemoved = true;
        } else {
          // A lapsed plan cannot read an article's shared episode: stop
          // watching it (a no-op for lecture episodes, which keep failing
          // over to the backoff as before).
          if (code === 'AUDIO_OVERVIEW_UPGRADE_REQUIRED') {
            dispatch(articleAudioOverviewDropped(ids[index]));
          }
          failed = true;
        }
      });

      if (items.length > 0) {
        dispatch(audioOverviewsUpserted({ items, requestedAt }));
      }

      // A bare 404 is also what every route answers once the feature is
      // switched off for this account: ask /config, which hides every
      // surface (and so stops this poller) if that is why.
      if (wasRemoved) void loadAudioOverviewConfig(dispatch);

      failuresRef.current = failed ? failuresRef.current + 1 : 0;
      schedule();
    };

    const onVisibilityChange = () => {
      if (isDocumentHidden()) {
        clearTimer();
      } else if (timer === null) {
        void poll();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    schedule();

    return () => {
      cancelled = true;
      clearTimer();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [dispatch, enabled, pendingKey]);
};
